'use client'

import { LineBriefcase } from '@qr-front/icons/line-briefcase'
import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { LineChevronRightSmall } from '@qr-front/icons/line-chevron-right-small'
import { LineEmail } from '@qr-front/icons/line-email'
import { LineEnterprise } from '@qr-front/icons/line-enterprise'
import { LineFileList } from '@qr-front/icons/line-file-list'
import { LineLinkAlt01 } from '@qr-front/icons/line-link-alt-01'
import { LineLocationPin } from '@qr-front/icons/line-location-pin'
import { LinePhone } from '@qr-front/icons/line-phone'
import { LineThumbsUp } from '@qr-front/icons/line-thumbs-up'
import { LineUser } from '@qr-front/icons/line-user'
import { Separator } from '@qr-front/ui/components/Separator'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import * as Accordion from '@radix-ui/react-accordion'
import {
	contactInfoEmailListAtom,
	contactInfoPhoneListAtom,
	contactInfoWebsiteAtom,
	toLabeledEntries,
} from 'features/generator/contentFormSections/CommonSections/ContactSection'
import { addressUrlAtom } from 'features/generator/contentFormSections/LocationForm/addressAtoms'
import { summaryAtom } from 'features/generator/contentFormSections/SummaryForm/SummarySection'
import { SocialLinkItem } from 'features/generator/qrTypes/SocialMedia/SocialMediaScreen/SocialMediaScreenSections/SocialLinkItem'
import { useAtomValue } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import {
	Fragment,
	ReactElement,
	ReactNode,
	useEffect,
	useMemo,
	useState,
} from 'react'

import { TSvgIcon } from '@/types'

import { addMissingUrlProtocol } from '../../../../../../utils/urlUtils'
import {
	aboutCompanyNameAtom,
	aboutProfessionAtom,
	socialLinkListAtom,
	TSocialLinkField,
} from '../../../../contentFormSections'
import {
	useFullLocation,
	useFullLocationUrl,
} from '../../../../contentFormSections/LocationForm'
import { QrScreen, TQrScreenProps } from '../../../../QrScreen/QrScreen'
import { ContactItem, SectionIcon } from './ContactItem'
import { VCardHeader } from './VCardHeader'
import { IsPreviewModeProvider } from './vCardScreenContexts'
import { vCardPrimaryColorAtom } from './VCardScreenState'

const SectionListContainer = ({ children }: { children: ReactNode }) => (
	<ul className="flex flex-col gap-4">{children}</ul>
)

const SectionChevron = getVersionForClone({
	base: LineChevronRightSmall,
	clone1: LineChevronDownSmall,
})

const Section = ({
	title,
	Icon,
	content,
	isOpen,
}: {
	title: string
	Icon: TSvgIcon
	content: ReactElement
	isOpen: boolean
}) => (
	<Accordion.Item
		key={title}
		value={title}
		className="group rounded-6 bg-white shadow-generator"
	>
		<Accordion.Trigger
			className={cn(
				'type-body-small flex w-full items-center justify-between gap-2 p-2 transition-colors',
				getVersionForClone({
					base: '[&[data-state=open]>svg]:rotate-90',
					clone1: '[&[data-state=open]>svg]:rotate-180',
				}),
			)}
		>
			<div className="flex items-center gap-2 text-left">
				<SectionIcon Icon={Icon} />
				{title}
			</div>
			<SectionChevron className="size-4 text-grey transition-transform duration-200" />
		</Accordion.Trigger>
		<Separator
			collapse
			className="ml-2 w-[calc(100%_-_theme(spacing.4))] transition-opacity group-data-[state=closed]:opacity-0"
		/>
		<Accordion.Content forceMount>
			<AnimatePresence initial={false}>
				{isOpen ? (
					<motion.div
						key={title}
						initial={{ height: 0 }}
						animate={{ height: 'auto' }}
						exit={{ height: 0 }}
						className="overflow-hidden"
					>
						<div className="p-4">{content}</div>
					</motion.div>
				) : null}
			</AnimatePresence>
		</Accordion.Content>
	</Accordion.Item>
)

const ContactSection = () => {
	const t = useTranslations()

	const phoneList = toLabeledEntries(
		useAtomValue(contactInfoPhoneListAtom),
		t,
	)

	const phoneItem = phoneList.length ? (
		<ContactItem
			key="contact"
			contactItem={{
				Icon: LinePhone,
				value: phoneList,
				ValueWrapper: ({ value, className }) => (
					<a
						href={`tel:${value}`}
						title={value}
						className={className}
					>
						{value}
					</a>
				),
			}}
		/>
	) : null

	const emailList = toLabeledEntries(
		useAtomValue(contactInfoEmailListAtom),
		t,
	)

	const emailItem = emailList.length ? (
		<ContactItem
			key="email"
			contactItem={{
				Icon: LineEmail,
				value: emailList,
				ValueWrapper: ({ value, className }) => (
					<a
						href={`mailto:${value}`}
						title={value}
						className={className}
					>
						{value}
					</a>
				),
			}}
		/>
	) : null

	const websiteUrl = useAtomValue(contactInfoWebsiteAtom)

	const websiteItem = websiteUrl?.length ? (
		<ContactItem
			key="website"
			contactItem={{
				label: t(
					'generator__qr_content_screen__vcard_type__sections__contact__website',
				),
				Icon: LineLinkAlt01,
				value: websiteUrl,
				ValueWrapper: ({ value, className }) => (
					<a
						href={addMissingUrlProtocol(value)}
						target="_blank"
						rel="noreferrer"
						className={className}
					>
						{value}
					</a>
				),
			}}
		/>
	) : null

	const fullLocation = useFullLocation()
	const fullLocationUrl = useFullLocationUrl()

	const addressUrl = useAtomValue(addressUrlAtom)

	const locationItem =
		fullLocation.length || addressUrl ? (
			<ContactItem
				key="location"
				contactItem={{
					label: t(
						'generator__qr_content_screen__vcard_type__sections__contact__location',
					),
					Icon: LineLocationPin,
					value: addressUrl || fullLocation,
					ValueWrapper: ({ value, className }) => (
						<a
							href={
								addressUrl
									? addMissingUrlProtocol(addressUrl)
									: fullLocationUrl
							}
							target="_blank"
							rel="nofollow ugc noreferrer"
							className={className}
						>
							{value}
						</a>
					),
				}}
			/>
		) : null

	const itemList = [phoneItem, emailItem, websiteItem, locationItem].filter(
		(item) => item !== null,
	)

	return (
		<SectionListContainer>
			{itemList.map((item, index) =>
				itemList.length > 1 && index < itemList.length - 1 ? (
					<Fragment key={index}>
						{item}
						<Separator collapse />
					</Fragment>
				) : (
					item
				),
			)}
		</SectionListContainer>
	)
}

const CompanySection = () => {
	const t = useTranslations()

	const companyName = useAtomValue(aboutCompanyNameAtom)

	const companyNameItem = companyName?.length ? (
		<ContactItem
			key="company"
			contactItem={{
				label: t(
					'generator__qr_content_screen__vcard_type__sections__company__name',
				),
				Icon: LineEnterprise,
				value: companyName,
			}}
		/>
	) : null

	const profession = useAtomValue(aboutProfessionAtom)

	const professionItem = profession?.length ? (
		<ContactItem
			key="profession"
			contactItem={{
				label: t(
					'generator__qr_content_screen__vcard_type__sections__company__profession',
				),
				Icon: LineUser,
				value: profession,
			}}
		/>
	) : null

	const itemList = [companyNameItem, professionItem].filter(
		(item) => item !== null,
	)

	return (
		<SectionListContainer>
			{itemList.map((item, index) =>
				itemList.length > 1 && index < itemList.length - 1 ? (
					<Fragment key={index}>
						{item}
						<Separator collapse />
					</Fragment>
				) : (
					item
				),
			)}
		</SectionListContainer>
	)
}

const SummarySection = () => {
	const summary = useAtomValue(summaryAtom)

	return summary?.length ? (
		<p className="type-body-caption break-words">{summary}</p>
	) : null
}

const SocialSection = () => {
	const socialLinkList = useAtomValue(socialLinkListAtom)

	return (
		<ul className="flex flex-col gap-2">
			{socialLinkList.map((item: TSocialLinkField, index) => (
				<SocialLinkItem
					key={item.platformId}
					item={item}
					index={index}
				/>
			))}
		</ul>
	)
}

export const Content = ({ previewMode, demoMode }: TQrScreenProps) => {
	const t = useTranslations()

	const phoneList = useAtomValue(contactInfoPhoneListAtom)
	const website = useAtomValue(contactInfoWebsiteAtom)
	const emailList = useAtomValue(contactInfoEmailListAtom)
	const fullLocation = useFullLocation()
	const addressUrl = useAtomValue(addressUrlAtom)

	const hasContactItems =
		phoneList.filter((phone) => phone.value.length).length ||
		website ||
		emailList.filter((email) => email.value.length).length ||
		fullLocation ||
		addressUrl ||
		demoMode

	const contactSection = useMemo(
		() =>
			hasContactItems
				? {
						title: t(
							'generator__qr_content_screen__vcard_type__sections__contact__title',
						),
						Icon: LinePhone,
						content: <ContactSection />,
					}
				: null,
		[hasContactItems, t],
	)

	const companyName = useAtomValue(aboutCompanyNameAtom)
	const profession = useAtomValue(aboutProfessionAtom)

	const hasCompanyItems = companyName || profession || demoMode

	const companySection = useMemo(
		() =>
			hasCompanyItems
				? {
						title: t(
							'generator__qr_content_screen__vcard_type__sections__company__title',
						),
						Icon: LineBriefcase,
						content: <CompanySection />,
					}
				: null,
		[hasCompanyItems, t],
	)

	const hasSummary = useAtomValue(summaryAtom)?.length || demoMode

	const summarySection = useMemo(
		() =>
			hasSummary
				? {
						title: t(
							'generator__qr_content_screen__vcard_type__sections__summary__title',
						),
						Icon: LineFileList,
						content: <SummarySection />,
					}
				: null,
		[hasSummary, t],
	)

	const hasSocialLinks = useAtomValue(socialLinkListAtom).length || demoMode

	const socialSection = useMemo(
		() =>
			hasSocialLinks
				? {
						title: t(
							'generator__qr_content_screen__vcard_type__sections__social__title',
						),
						Icon: LineThumbsUp,
						content: <SocialSection />,
					}
				: null,
		[hasSocialLinks, t],
	)

	const sectionList = useMemo(
		() =>
			[
				contactSection,
				companySection,
				summarySection,
				socialSection,
			] as const satisfies ({
				title: string
				Icon: TSvgIcon
				content: ReactElement
			} | null)[],
		[companySection, contactSection, socialSection, summarySection],
	)

	const [openSectionList, setOpenSectionList] = useState<string[]>([])

	useEffect(() => {
		if (previewMode && !demoMode) {
			setOpenSectionList(
				sectionList
					.filter((section) => section !== null)
					.map(({ title }) => title),
			)
		}
	}, [demoMode, previewMode, sectionList])

	return (
		<IsPreviewModeProvider value={previewMode}>
			<div className={cn('relative', demoMode && 'pointer-events-none')}>
				<VCardHeader previewMode={previewMode} demoMode={demoMode} />
				<Accordion.Root
					type="multiple"
					value={openSectionList}
					onValueChange={setOpenSectionList}
					className="mx-auto -mt-6 mb-6 flex max-w-qr-screen-constrained flex-col gap-2 px-5"
				>
					{sectionList
						.filter((value) => value !== null)
						.map(({ title, content, Icon }) => (
							<Section
								key={title}
								title={title}
								Icon={Icon}
								content={content}
								isOpen={openSectionList.includes(title)}
							/>
						))}
				</Accordion.Root>
			</div>
		</IsPreviewModeProvider>
	)
}

export const VCardScreen = (props: TQrScreenProps) => (
	<QrScreen
		{...props}
		primaryColor={useAtomValue(vCardPrimaryColorAtom)}
		content={<Content {...props} />}
	/>
)
